import clsx from 'clsx';
import React, { ReactNode, useState } from 'react';
import EyeLock from '../../assets/icons/eye-close.svg?react';
import EyeLockUnlock from '../../assets/icons/eye-open.svg?react';

interface InputProps {
  otherClasses?: {
    input?: string;
    container?: string;
    icon?: string;
    label?: string;
  };
  value: string | number;
  errorMessage?: string;
  label?: string | ReactNode;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: any) => void;
  icon?: ReactNode;
  placeholder: string;
  name: string;
  type: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'time';
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  [key: string]: any;
}

const Input: React.FC<InputProps> = ({
  otherClasses,
  icon,
  errorMessage = '',
  type,
  value,
  name,
  onBlur,
  label,
  onChange,
  disabled = false,
  placeholder,
  'aria-required': ariaRequired,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  autoComplete,
  ...rest
}) => {
  const inputContainer = clsx(
    otherClasses?.container,
    'flex flex-col gap-2.5 items-start relative w-fit',
  );

  const [passwordToggle, setPasswordToggle] = useState<boolean>(false);
  const errorId = `${name}-error`;

  const inputClasses = clsx(
    otherClasses?.input,
    'w-full h-60 global-transition rounded-10 placeholder:text-neutral-tones-text/50 text-neutral-tones-text ring -tracking-form-input p-5 text-base font-normal font-inter-variable  outline-none disabled:ring-neutral-tones-text/35 disabled:placeholder:text-neutral-tones-text/35 disabled:text-neutral-tones-text/35 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
    icon ? 'pr-14 pl-5' : 'px-5',
    errorMessage !== ''
      ? 'ring-brand-posh-pink ring-2'
      : 'ring-neutral-tones-text/50 focus:ring-2 focus:ring-brand-byzantine',
  );
  const iconClasses = clsx(
    otherClasses?.icon,
    'absolute right-5 translate-y-[-50%]',
    label ? ' top-[64%]' : 'top-2/4',
  );
  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label  font-bold -tracking-form-label font-inter-variable',
    disabled
      ? 'text-neutral-tones-text/35 cursor-not-allowed'
      : 'text-neutral-tones-text',
  );

  const commonInputProps = {
    disabled,
    name,
    id: name,
    value,
    onBlur,
    autoComplete: type === 'password' ? 'new-password' : autoComplete,
    onChange,
    className: inputClasses,
    placeholder,
    'aria-required': ariaRequired,
    'aria-invalid': ariaInvalid ?? errorMessage !== '',
    'aria-describedby': errorMessage ? errorId : ariaDescribedby,
    ...rest,
  };

  if (type === 'password') {
    return (
      <div className={inputContainer} data-testid="input">
        {label && (
          <label htmlFor={name} className={labelClasses}>
            {label}
          </label>
        )}
        <input
          {...commonInputProps}
          type={passwordToggle ? 'text' : type}
          className={clsx(inputClasses, 'pr-14')}
        />
        <button
          type="button"
          onClick={() => setPasswordToggle(!passwordToggle)}
          className={iconClasses}
          aria-label={passwordToggle ? 'Hide password' : 'Show password'}
        >
          {passwordToggle ? (
            <EyeLockUnlock
              className={clsx(
                'h-8 w-8',
                disabled
                  ? '[&>path]:fill-neutral-tones-text/35'
                  : '[&>path]:fill-neutral-tones-text',
              )}
              aria-hidden="true"
            />
          ) : (
            <EyeLock
              className={clsx(
                'h-8 w-8',
                disabled
                  ? '[&>path]:fill-neutral-tones-text/35'
                  : '[&>path]:fill-neutral-tones-text',
              )}
              aria-hidden="true"
            />
          )}
        </button>
        {errorMessage !== '' && (
          <span
            id={errorId}
            role="alert"
            className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal"
          >
            {errorMessage}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className={inputContainer} data-testid="input">
      {label && (
        <label htmlFor={name} className={labelClasses}>
          {label}
        </label>
      )}
      <input {...commonInputProps} type={type} />
      {icon && (
        <div className={iconClasses} aria-hidden="true">
          {icon}
        </div>
      )}
      {errorMessage !== '' && (
        <span
          id={errorId}
          role="alert"
          className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal"
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default Input;
